import React, {
  type ReactNode,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { UserRole } from "@shared/types";

// Hoist UserRole import into module scope so filterNavTree (above component) can use it.
// (This import is already above; the duplicate re-assert is intentional documentation only.)
import NavBar from "./NavBar";
import { useAuth } from "../hooks/useAuth";
import { useTheme, type ThemePreference } from "../hooks/useTheme";

interface LayoutProps {
  children: ReactNode;
  role: UserRole;
}

// ── Icons ────────────────────────────────────────────────────────────────────

function HomeIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function SyncIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 21h5v-5" />
    </svg>
  );
}

function UsersIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function UserCheckIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <polyline points="16 11 18 13 22 9" />
    </svg>
  );
}

function TrendingUpIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

function BookOpenIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function PackageIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function HeartPulseIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
      <path d="M3.22 12H9.5l1.5-3 2 6 1.5-3.5H21" />
    </svg>
  );
}

function TruckIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3" />
      <rect x="9" y="11" width="14" height="10" rx="2" />
      <circle cx="12" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
    </svg>
  );
}

function StarIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function GlobeIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function CalendarIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function MapPinIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function ShieldIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function SettingsIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function LogoutIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function ChevronLeftIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ChevronDownIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function BrainIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
    </svg>
  );
}

function MegaphoneIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

function BuildingIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" />
    </svg>
  );
}

function BriefcaseIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function HamburgerIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function XIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ThemeIcon({
  theme,
  size = 18,
}: {
  theme: ThemePreference;
  size?: number;
}) {
  if (theme === "dark") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    );
  }
  if (theme === "light") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

const THEME_LABELS: Record<ThemePreference, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

// ── Nav structure ─────────────────────────────────────────────────────────────

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactElement;
  comingSoon?: false;
  /** Hide from non-(board_member|admin) users even when the parent committee
   *  is visible to all roles (e.g. an Intervention link inside FORGE). */
  boardOnly?: boolean;
  /** Hide from non-admin users — used sparingly inside a non-adminOnly group. */
  adminOnly?: boolean;
  /** Gate on the server-resolved view_members permission (member PII pages —
   *  Member Engagement, Retention). Mirrors the API requirePermission gate so a
   *  viewer can't navigate to a page whose data the API would 403. S-CLIENT. */
  requiresViewMembers?: boolean;
  /** Gate on the server-resolved view_applicants permission (applicant PII). */
  requiresViewApplicants?: boolean;
}

interface ComingSoonNavItem {
  to: string;
  label: string;
  icon: React.ReactElement;
  comingSoon: true;
  boardOnly?: boolean;
  adminOnly?: boolean;
  requiresViewMembers?: boolean;
  requiresViewApplicants?: boolean;
}

/** A non-link label that visually buckets the items beneath it inside a
 *  committee group (e.g. Finance → Position / Budget / Spend & Vendors /
 *  Governance & Reporting). Not a route; never highlights or persists. */
interface SubheadingNavItem {
  kind: "subheading";
  label: string;
}

type AnyNavItem = NavItem | ComingSoonNavItem | SubheadingNavItem;

/** Type guard: is this a real navigable link (has a `to`)? */
function isLinkItem(it: AnyNavItem): it is NavItem | ComingSoonNavItem {
  return !("kind" in it && it.kind === "subheading");
}

interface TopLevelNav {
  /** Direct link — rendered as a single row, no expand. */
  kind: "link";
  item: NavItem;
}

interface CommitteeNav {
  /** Expandable committee group. Mirrors DCVFD org chart — FORGE, Membership,
   *  Operations, etc. — with sub-items surfaced as a collapsible list. */
  kind: "committee";
  key: string; // stable id for persistence
  heading: string;
  icon: React.ReactElement;
  items: AnyNavItem[];
  /** If true, only visible when user.role === 'admin'. */
  adminOnly?: boolean;
  /**
   * If true, only visible when user.role is `board_member` or `admin`.
   * Used for Human Resources and any other Board-restricted surface.
   */
  boardOnly?: boolean;
  /**
   * If true, gated on the server-resolved `view_finance` permission instead of
   * role. Lets viewer-role paid staff (Entra 'employees' group) see Finance
   * while general members do not. See migration 0062 + /api/auth/me.
   */
  requiresViewFinance?: boolean;
  /** Optional hint: short one-liner shown on hover. */
  hint?: string;
}

interface SectionNav {
  /** Collapsible super-grouping. Operations + Administration use these to
   *  bucket their member committees. State persists in localStorage; default
   *  is collapsed so non-operational users don't get overwhelmed. */
  kind: "section";
  key: string;
  label: string;
  /** When true, the section header only renders as a collapsible divider
   *  (no children-peek while closed). Both sections use this. */
  collapsible: boolean;
  defaultOpen: boolean;
  children: Array<TopLevelNav | CommitteeNav>;
}

type NavNode = TopLevelNav | CommitteeNav | SectionNav;

// Committee-based IA — matches the authoritative DCVFD org structure as
// maintained in SharePoint. Committees are alphabetical (after Overview +
// FORGE, which rank first by importance), and Operations is its own section
// containing the four line functions: EMS, Suppression, Special Operations,
// Training.
//
// Placeholder items (`comingSoon: true`) are telegraphed visually but not yet
// linked — they stake out the slot for future pages so the nav doesn't churn.
const NAV_TREE: NavNode[] = [
  // Top-level: always visible, never inside a section.
  {
    kind: "link",
    item: { to: "/", label: "Overview", icon: <HomeIcon /> },
  },
  {
    kind: "committee",
    key: "forge",
    heading: "FORGE",
    icon: <MegaphoneIcon />,
    hint: "Recruitment program — spans operations and administration",
    items: [
      { to: "/forge", label: "Pipeline Overview", icon: <TrendingUpIcon /> },
      { to: "/forge/interest", label: "Interest", icon: <GlobeIcon /> },
      { to: "/forge/application", label: "Application", icon: <UsersIcon /> },
      { to: "/forge/bod-vote", label: "BOD Vote", icon: <StarIcon /> },
      { to: "/forge/training", label: "Training", icon: <BookOpenIcon /> },
      {
        to: "/forge/year-one",
        label: "Year 1 Active",
        icon: <UserCheckIcon />,
      },
      {
        to: "/forge/interventions",
        label: "Interventions",
        icon: <StarIcon />,
        boardOnly: true,
      },
      {
        to: "/forge/outreach-signals",
        label: "Outreach Triage",
        icon: <CalendarIcon />,
      },
      {
        to: "/forge/forecast",
        label: "Applicant Forecast",
        icon: <StarIcon />,
      },
      {
        to: "/retention",
        label: "Retention",
        icon: <TrendingUpIcon />,
        requiresViewMembers: true,
      },
      {
        to: "/retention/root-cause",
        label: "Root Cause",
        icon: <TrendingUpIcon />,
      },
      {
        to: "/retention/risk",
        label: "Retention Risk",
        icon: <TrendingUpIcon />,
      },
      {
        to: "/applicants",
        label: "Applicant Tracker",
        icon: <CalendarIcon />,
        requiresViewApplicants: true,
      },
    ],
  },

  // ─── OPERATIONS (collapsible, closed by default) ────────────────────────
  {
    kind: "section",
    key: "operations",
    label: "Operations",
    collapsible: true,
    defaultOpen: false,
    children: [
      {
        kind: "committee",
        key: "apparatus",
        heading: "Apparatus & Maintenance",
        icon: <TruckIcon />,
        hint: "Fleet status, apparatus lifecycle, maintenance log",
        items: [
          { to: "/fleet", label: "Fleet & Apparatus", icon: <TruckIcon /> },
          {
            to: "/apparatus/maintenance",
            label: "Maintenance Log",
            icon: <SettingsIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "irons",
        heading: "IRONS",
        icon: <PackageIcon />,
        hint: "Incident reporting, issue tracking",
        items: [
          { to: "/irons", label: "IRONS Dashboard", icon: <PackageIcon /> },
        ],
      },
      {
        kind: "committee",
        key: "ops-ems",
        heading: "EMS",
        icon: <HeartPulseIcon />,
        hint: "EMS supply, ALS/BLS resources, medic operations",
        items: [
          { to: "/ems-supply", label: "EMS Supply", icon: <HeartPulseIcon /> },
          {
            to: "/ems/als-resources",
            label: "ALS Resources",
            icon: <HeartPulseIcon />,
            comingSoon: true,
          },
          {
            to: "/ems/medic-ops",
            label: "Medic Operations",
            icon: <HeartPulseIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "ops-suppression",
        heading: "Suppression",
        icon: <ShieldIcon />,
        hint: "Fire suppression, unit readiness, staffing",
        items: [
          {
            to: "/suppression/incidents",
            label: "Incident Log",
            icon: <PackageIcon />,
            comingSoon: true,
          },
          {
            to: "/suppression/readiness",
            label: "Unit Readiness",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
          {
            to: "/staffing",
            label: "Staffing Coverage",
            icon: <CalendarIcon />,
          },
          {
            to: "/ops/staffing-capacity",
            label: "Staffing Capacity",
            icon: <UsersIcon />,
          },
        ],
      },
      {
        kind: "committee",
        key: "ops-special",
        heading: "Special Operations",
        icon: <StarIcon />,
        hint: "Tech rescue, hazmat, water rescue, other specialty teams",
        items: [
          {
            to: "/specops/teams",
            label: "Specialty Teams",
            icon: <StarIcon />,
            comingSoon: true,
          },
          {
            to: "/specops/deployments",
            label: "Deployments",
            icon: <MapPinIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "ops-training",
        heading: "Training",
        icon: <BookOpenIcon />,
        hint: "Department training — CPR, continuing education, certifications",
        items: [
          {
            to: "/forge/training",
            label: "Recruit Training",
            icon: <BookOpenIcon />,
          },
          {
            to: "/training/cpr",
            label: "CPR Instructor",
            icon: <HeartPulseIcon />,
            comingSoon: true,
          },
          {
            to: "/training/continuing",
            label: "Continuing Education",
            icon: <BookOpenIcon />,
            comingSoon: true,
          },
          {
            to: "/training/certs",
            label: "Certifications",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
        ],
      },
    ],
  },

  // ─── ADMINISTRATION (collapsible, closed by default) ────────────────────
  {
    kind: "section",
    key: "administration",
    label: "Administration",
    collapsible: true,
    defaultOpen: false,
    children: [
      {
        kind: "committee",
        key: "associates-auxiliary",
        heading: "Associates & Auxiliary",
        icon: <UsersIcon />,
        hint: "Associate members, Cadets, Auxiliary members",
        items: [
          {
            to: "/associates/roster",
            label: "Associate Roster",
            icon: <UsersIcon />,
            comingSoon: true,
          },
          {
            to: "/associates/cadets",
            label: "Cadet Program",
            icon: <UserCheckIcon />,
            comingSoon: true,
          },
          {
            to: "/associates/auxiliary",
            label: "Auxiliary",
            icon: <UsersIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "board",
        heading: "Board of Directors",
        icon: <BuildingIcon />,
        boardOnly: true,
        hint: "Board Members + Admins — pre-meeting brief, strategic reviews, governance signals",
        items: [
          { to: "/bod", label: "BOD Brief", icon: <BrainIcon /> },
          { to: "/strategic", label: "Strategic Reviews", icon: <BrainIcon /> },
          {
            to: "/external-signals",
            label: "External Signals",
            icon: <TrendingUpIcon />,
          },
          {
            to: "/viability/scenarios",
            label: "Viability Scenarios",
            icon: <TrendingUpIcon />,
          },
          {
            to: "/board/officers",
            label: "Officers",
            icon: <StarIcon />,
            comingSoon: true,
          },
          {
            to: "/board/minutes",
            label: "Executive Minutes",
            icon: <BookOpenIcon />,
            comingSoon: true,
          },
          {
            to: "/board/succession",
            label: "Succession Packages",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "bylaws",
        heading: "Bylaws & Policy",
        icon: <BookOpenIcon />,
        hint: "Institutional memory, policy library, bylaws",
        items: [
          { to: "/memory", label: "Institutional Memory", icon: <BrainIcon /> },
          {
            to: "/policy/library",
            label: "Policy Library",
            icon: <BookOpenIcon />,
            comingSoon: true,
          },
          {
            to: "/bylaws",
            label: "Bylaws",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "community-engagement",
        heading: "Community Engagement",
        icon: <GlobeIcon />,
        hint: "Community events, public outreach, sponsorships",
        items: [
          { to: "/community", label: "Community Events", icon: <GlobeIcon /> },
          {
            to: "/community/outreach",
            label: "Public Outreach",
            icon: <MegaphoneIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "desanto-hall",
        heading: "DeSanto Hall",
        icon: <BuildingIcon />,
        hint: "Hall rentals, event calendar, fundraising venue",
        items: [
          {
            to: "/desanto/rentals",
            label: "Rentals & Calendar",
            icon: <CalendarIcon />,
            comingSoon: true,
          },
          {
            to: "/desanto/bookings",
            label: "Booking Management",
            icon: <BookOpenIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "facilities",
        heading: "Facilities",
        icon: <BuildingIcon />,
        hint: "Buildings, house & grounds, station compliance",
        items: [
          {
            to: "/facilities/house-grounds",
            label: "House & Grounds",
            icon: <BuildingIcon />,
            comingSoon: true,
          },
          {
            to: "/facilities/compliance",
            label: "Station Compliance",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "finance",
        heading: "Finance",
        icon: <TrendingUpIcon />,
        // Gated on the server-resolved view_finance permission (not role), so
        // viewer-role paid staff in the 'employees' group see it while general
        // members (alldcvfd) do not. See migration 0062 + useAuth permissions.
        requiresViewFinance: true,
        hint: "Board + staff only — levy funds (Mobius), non-profit funds (Truist/QuickBooks), reconciliation, board reporting",
        // Grouped into four labeled sub-sections (Position · Budget · Spend &
        // Vendors · Governance & Reporting) so the 12-item list reads as four
        // logical buckets. Subheadings are presentational only — every route +
        // gate is unchanged.
        items: [
          { kind: "subheading", label: "Position" },
          {
            to: "/finance/overview",
            label: "Overview",
            icon: <TrendingUpIcon />,
          },
          // The two fund universes — kept visually + structurally separate; never commingled.
          { to: "/finance", label: "Levy Funds", icon: <TrendingUpIcon /> },
          {
            to: "/finance/non-profit",
            label: "Non-Profit Funds",
            icon: <BuildingIcon />,
          },

          { kind: "subheading", label: "Budget" },
          {
            to: "/finance/fy27-v2",
            label: "FY27 Budget",
            icon: <CalendarIcon />,
          },
          {
            to: "/finance/deficit",
            label: "Deficit & Carryover",
            icon: <TrendingUpIcon />,
          },
          { to: "/finance/projects", label: "Projects", icon: <PackageIcon /> },
          {
            to: "/finance/history",
            label: "Multi-Year History",
            icon: <TrendingUpIcon />,
          },

          { kind: "subheading", label: "Spend & Vendors" },
          {
            to: "/finance/purchase-orders",
            label: "Purchase Orders",
            icon: <PackageIcon />,
          },
          {
            to: "/finance/vendors",
            label: "Vendor Analysis",
            icon: <PackageIcon />,
          },

          { kind: "subheading", label: "Governance & Reporting" },
          {
            to: "/finance/alerts",
            label: "Variance & Alerts",
            icon: <ShieldIcon />,
          },
          {
            to: "/finance/approvals",
            label: "Approvals",
            icon: <StarIcon />,
          },
          {
            to: "/finance/board-packet",
            label: "Board Review",
            icon: <BookOpenIcon />,
          },
        ],
      },
      {
        kind: "committee",
        key: "hr",
        heading: "Human Resources",
        icon: <BriefcaseIcon />,
        boardOnly: true,
        hint: "Board Members only — career staff, compliance, corporate docs",
        items: [
          {
            to: "/hr/career-staff",
            label: "Career Staff",
            icon: <BriefcaseIcon />,
            comingSoon: true,
          },
          {
            to: "/hr/compliance",
            label: "HR Compliance",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
          {
            to: "/hr/corporate-docs",
            label: "Corporate Docs",
            icon: <BuildingIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "insurance",
        heading: "Insurance",
        icon: <ShieldIcon />,
        hint: "Coverage, claims, risk management",
        items: [
          {
            to: "/insurance/coverage",
            label: "Coverage",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
          {
            to: "/insurance/claims",
            label: "Claims & Incidents",
            icon: <PackageIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "logistics",
        heading: "Logistics & Equipment",
        icon: <PackageIcon />,
        hint: "Equipment inventory, gear, PPE compliance",
        items: [
          { to: "/equipment", label: "Equipment & Gear", icon: <ShieldIcon /> },
          {
            to: "/logistics/ppe-compliance",
            label: "PPE Compliance",
            icon: <ShieldIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "marketing-comms",
        heading: "Marketing & Communications",
        icon: <MegaphoneIcon />,
        hint: "Brand, social media, press, internal comms",
        items: [
          {
            to: "/comms/social",
            label: "Social Channels",
            icon: <MegaphoneIcon />,
            comingSoon: true,
          },
          {
            to: "/comms/press",
            label: "Press & Releases",
            icon: <BookOpenIcon />,
            comingSoon: true,
          },
          {
            to: "/comms/internal",
            label: "Internal Bulletins",
            icon: <MegaphoneIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "membership",
        heading: "Membership",
        icon: <UsersIcon />,
        hint: "Article V §F — roster, engagement, applications",
        items: [
          {
            to: "/membership",
            label: "Member Engagement",
            icon: <UsersIcon />,
            requiresViewMembers: true,
          },
          {
            to: "/membership/roster",
            label: "Full Roster",
            icon: <UserCheckIcon />,
            comingSoon: true,
          },
          {
            to: "/membership/applications",
            label: "Applications",
            icon: <BookOpenIcon />,
            comingSoon: true,
          },
        ],
      },
      {
        kind: "committee",
        key: "ways-and-means",
        heading: "Ways & Means",
        icon: <StarIcon />,
        hint: "Fundraising, grants, rental coordination",
        items: [
          {
            to: "/ways-means/fundraising",
            label: "Fundraising",
            icon: <StarIcon />,
            comingSoon: true,
          },
          {
            to: "/ways-means/grants",
            label: "Grants",
            icon: <BookOpenIcon />,
            comingSoon: true,
          },
        ],
      },
    ],
  },

  // ─── SETTINGS — platform-level tooling. ADMIN ONLY. ──────────────────────
  // Sync health, cross-org GitHub state, AI platform observability, user
  // management, and the engineering outstanding-work feed all live here.
  // Board members do not see this group.
  {
    kind: "committee",
    key: "settings",
    heading: "Settings",
    icon: <SettingsIcon />,
    adminOnly: true,
    hint: "Platform tooling — AI engine, sync health, cross-org status, users (admin only)",
    items: [
      { to: "/admin/ai", label: "AI Platform", icon: <BrainIcon /> },
      { to: "/cross-org", label: "Cross-org Status", icon: <GlobeIcon /> },
      { to: "/outstanding", label: "Outstanding Work", icon: <PackageIcon /> },
      { to: "/sync", label: "Sync Management", icon: <SyncIcon /> },
      { to: "/users", label: "User Management", icon: <UserCheckIcon /> },
    ],
  },
];

// ── User avatar ──────────────────────────────────────────────────────────────

function UserAvatar({
  photoUrl,
  name,
  size = 36,
}: {
  photoUrl: string | null;
  name: string;
  size?: number;
}) {
  const initial = name.trim().charAt(0).toUpperCase();
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        width={size}
        height={size}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-dcvfd-accent flex items-center justify-center shrink-0 text-white font-semibold select-none"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initial}
    </div>
  );
}

// ── Shared nav item renderer ──────────────────────────────────────────────────

/** A small uppercase divider label that buckets the links beneath it inside a
 *  committee group. Collapses to a thin rule when the sidebar is icons-only. */
function SidebarSubheading({
  label,
  collapsed,
}: {
  label: string;
  collapsed: boolean;
}) {
  if (collapsed) {
    return <div className="border-t border-theme-subtle my-2 mx-2" />;
  }
  return (
    <p className="px-3 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-theme-muted select-none">
      {label}
    </p>
  );
}

/** Render a committee group's items, switching subheading markers to dividers
 *  and real links to nav items. Keeps the four map sites (desktop/mobile ×
 *  top-level/section) consistent. */
function renderCommitteeItems(
  items: AnyNavItem[],
  collapsed: boolean,
  onClick?: () => void,
) {
  return items.map((item, idx) =>
    !isLinkItem(item) ? (
      <SidebarSubheading
        key={`sub:${item.label}:${idx}`}
        label={item.label}
        collapsed={collapsed}
      />
    ) : (
      <SidebarNavItem
        key={item.to}
        item={item}
        collapsed={collapsed}
        onClick={onClick}
      />
    ),
  );
}

function SidebarNavItem({
  item,
  collapsed,
  onClick,
}: {
  item: NavItem | ComingSoonNavItem;
  collapsed: boolean;
  onClick?: () => void;
}) {
  if (item.comingSoon) {
    return (
      <div
        key={item.label}
        title={collapsed ? item.label : undefined}
        className={`flex items-center rounded-lg text-sm font-medium text-theme-muted opacity-35 pointer-events-none select-none ${
          collapsed ? "justify-center px-0 py-2.5 w-full" : "gap-3 px-3 py-2.5"
        }`}
      >
        {item.icon}
        {!collapsed && <span className="truncate">{item.label}</span>}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      // Always use exact matching so a parent like /forge doesn't stay
      // highlighted when the user navigates to a child like /forge/interest.
      // Committee-group highlighting (isCommitteeActive) still uses prefix
      // logic, so the parent heading remains visually active even though
      // the individual Pipeline Overview link deactivates correctly.
      end
      title={collapsed ? item.label : undefined}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center rounded-lg text-sm font-medium transition-colors ${
          collapsed ? "justify-center px-0 py-2.5 w-full" : "gap-3 px-3 py-2.5"
        } ${
          isActive
            ? "bg-dcvfd text-white"
            : "text-theme-secondary hover:text-theme-primary hover:bg-theme-overlay"
        }`
      }
    >
      {item.icon}
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

const SIDEBAR_COLLAPSED_KEY = "dcvfd-sidebar-collapsed";
const COMMITTEE_OPEN_KEY = "dcvfd-committee-open";
const SECTION_OPEN_KEY = "dcvfd-section-open";
const SIDEBAR_EXPANDED_W = 256;
const SIDEBAR_COLLAPSED_W = 64;

/** Flat walk over committees across top-level and section children. */
function collectCommittees(tree: NavNode[]): CommitteeNav[] {
  const out: CommitteeNav[] = [];
  for (const node of tree) {
    if (node.kind === "committee") out.push(node);
    else if (node.kind === "section") {
      for (const child of node.children) {
        if (child.kind === "committee") out.push(child);
      }
    }
  }
  return out;
}

/** Role-gate a nav tree — drops committees the user can't see; also filters
 *  inside section children + per-item gates. Sections stay visible if they
 *  have at least one visible child. */
function filterNavTree(
  tree: NavNode[],
  role: UserRole | undefined,
  perms?: {
    view_finance: boolean;
    view_members: boolean;
    view_applicants: boolean;
  },
): NavNode[] {
  const canAccess = (node: CommitteeNav): boolean => {
    if (node.adminOnly && role !== "admin") return false;
    if (node.boardOnly && role !== "admin" && role !== "board_member")
      return false;
    if (node.requiresViewFinance && !perms?.view_finance) return false;
    return true;
  };
  const canAccessItem = (it: AnyNavItem): boolean => {
    // Subheadings are presentational dividers — always visible (their grouped
    // links carry the real gates).
    if (!isLinkItem(it)) return true;
    if (it.adminOnly && role !== "admin") return false;
    if (it.boardOnly && role !== "admin" && role !== "board_member")
      return false;
    if (it.requiresViewMembers && !perms?.view_members) return false;
    if (it.requiresViewApplicants && !perms?.view_applicants) return false;
    return true;
  };
  const filterCommittee = (node: CommitteeNav): CommitteeNav | null => {
    if (!canAccess(node)) return null;
    const items = node.items.filter(canAccessItem);
    if (items.length === 0) return null;
    return { ...node, items };
  };
  const out: NavNode[] = [];
  for (const node of tree) {
    if (node.kind === "link") out.push(node);
    else if (node.kind === "committee") {
      const c = filterCommittee(node);
      if (c) out.push(c);
    } else if (node.kind === "section") {
      const filteredChildren = node.children
        .map((c) => {
          if (c.kind === "link") return c;
          return filterCommittee(c);
        })
        .filter((c): c is TopLevelNav | CommitteeNav => c !== null);
      if (filteredChildren.length > 0) {
        out.push({ ...node, children: filteredChildren });
      }
    }
  }
  return out;
}

/**
 * Which SECTIONS (Operations, Administration) are open in the sidebar.
 * Defaults: each section's `defaultOpen` value, unless the user has toggled it
 * — then that preference persists across reloads + login sessions.
 * Auto-opens the section containing the current route on first navigation.
 */
function useSectionOpenState(
  tree: NavNode[],
): [Record<string, boolean>, (key: string) => void] {
  const location = useLocation();

  // Seed from defaultOpen, overlay any persisted user prefs on top.
  const defaultOpen = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const node of tree) {
      if (node.kind === "section" && node.collapsible)
        out[node.key] = node.defaultOpen;
    }
    return out;
  }, [tree]);

  const [state, setState] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(SECTION_OPEN_KEY);
      if (raw)
        return {
          ...defaultOpen,
          ...(JSON.parse(raw) as Record<string, boolean>),
        };
    } catch {
      /* noop */
    }
    return defaultOpen;
  });

  // Auto-open the section containing the active route.
  useEffect(() => {
    for (const node of tree) {
      if (node.kind !== "section" || !node.collapsible) continue;
      const anyChildMatches = node.children.some(
        (c) =>
          (c.kind === "link" && location.pathname.startsWith(c.item.to)) ||
          (c.kind === "committee" &&
            c.items.some(
              (i) =>
                isLinkItem(i) &&
                !i.comingSoon &&
                location.pathname.startsWith(i.to),
            )),
      );
      if (anyChildMatches) {
        setState((prev) =>
          prev[node.key] ? prev : { ...prev, [node.key]: true },
        );
        break;
      }
    }
  }, [location.pathname, tree]);

  useEffect(() => {
    try {
      localStorage.setItem(SECTION_OPEN_KEY, JSON.stringify(state));
    } catch {
      /* noop */
    }
  }, [state]);

  const toggle = useCallback((key: string) => {
    setState((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return [state, toggle];
}

/**
 * Which committee groups are currently expanded in the sidebar.
 * Persisted to localStorage; auto-opens the group matching the active route on first mount.
 */
function useCommitteeOpenState(
  tree: NavNode[],
): [Record<string, boolean>, (key: string) => void] {
  const location = useLocation();
  const [state, setState] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(COMMITTEE_OPEN_KEY);
      if (raw) return JSON.parse(raw) as Record<string, boolean>;
    } catch {
      /* noop */
    }
    return {};
  });

  // Auto-open the committee containing the current route so users never land
  // on a page whose nav group is collapsed.
  useEffect(() => {
    for (const committee of collectCommittees(tree)) {
      if (
        committee.items.some(
          (i) =>
            isLinkItem(i) &&
            !i.comingSoon &&
            location.pathname.startsWith(i.to),
        )
      ) {
        setState((prev) =>
          prev[committee.key] ? prev : { ...prev, [committee.key]: true },
        );
        break;
      }
    }
  }, [location.pathname, tree]);

  useEffect(() => {
    try {
      localStorage.setItem(COMMITTEE_OPEN_KEY, JSON.stringify(state));
    } catch {
      /* noop */
    }
  }, [state]);

  const toggle = useCallback((key: string) => {
    setState((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return [state, toggle];
}

/** Top-level section header button (e.g. "Operations", "Administration").
 *  Uppercase brand-colored label with a chevron; click toggles section open. */
function SectionHeader({
  label,
  expanded,
  onToggle,
  collapsed,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  collapsed: boolean;
}) {
  if (collapsed) {
    // Icon-only sidebar: render as a subtle divider, non-interactive (children
    // rendered unconditionally so users can still reach them).
    return (
      <div className="my-2 mx-2 border-t border-theme-subtle" title={label} />
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold tracking-[0.12em] uppercase text-brand-adaptive hover:text-theme-primary transition-colors"
    >
      <span
        className={`opacity-70 transition-transform ${expanded ? "" : "-rotate-90"}`}
      >
        <ChevronDownIcon size={12} />
      </span>
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}

/** Section heading — collapsible pill with the committee's icon + chevron. */
function CommitteeHeading({
  node,
  expanded,
  onToggle,
  collapsed,
  active,
}: {
  node: CommitteeNav;
  expanded: boolean;
  onToggle: () => void;
  collapsed: boolean;
  active: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={collapsed ? node.heading : node.hint}
      className={`w-full flex items-center rounded-lg text-sm font-semibold transition-colors ${
        collapsed ? "justify-center px-0 py-2" : "gap-2.5 px-3 py-2"
      } ${
        active
          ? "text-theme-primary bg-theme-overlay"
          : "text-theme-secondary hover:text-theme-primary hover:bg-theme-overlay/60"
      }`}
      aria-expanded={expanded}
    >
      <span className="shrink-0 opacity-80">{node.icon}</span>
      {!collapsed && (
        <>
          <span className="flex-1 text-left tracking-tight">
            {node.heading}
          </span>
          <span
            className={`opacity-60 transition-transform ${expanded ? "" : "-rotate-90"}`}
          >
            <ChevronDownIcon />
          </span>
        </>
      )}
    </button>
  );
}

function Sidebar({
  collapsed,
  onToggleCollapse,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { user, logout } = useAuth();
  const { theme, resolvedTheme, cycleTheme } = useTheme();
  const location = useLocation();

  const visibleTree = useMemo(
    () => filterNavTree(NAV_TREE, user?.role, user?.permissions),
    [user?.role, user?.permissions],
  );

  const [openMap, toggleCommittee] = useCommitteeOpenState(visibleTree);
  const [sectionOpen, toggleSection] = useSectionOpenState(visibleTree);

  function isCommitteeActive(node: CommitteeNav): boolean {
    return node.items.some(
      (i) =>
        isLinkItem(i) && !i.comingSoon && location.pathname.startsWith(i.to),
    );
  }

  return (
    <aside
      className={`hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-30 sidebar-transition ${
        collapsed ? "w-16" : "w-64"
      } bg-theme-raised border-r border-theme-subtle`}
    >
      {/* ── Brand / Logo ─────────────────────────────────────────── */}
      {/* Collapsed: DCVFD patch (department mark). Expanded: theme-aware Ember
          wordmark — a light-optimized PNG in light theme, the dark mark in dark. */}
      <div className="flex items-center justify-center border-b border-theme-subtle shrink-0 py-5 px-3">
        {collapsed ? (
          <img
            src="https://assets.dcvfd.org/images/patch.svg"
            alt="DCVFD"
            className="w-9 h-9 object-contain sidebar-transition"
          />
        ) : resolvedTheme === "light" ? (
          <img
            src="/brand/ember-light.png"
            alt="Ember AI"
            className="h-16 w-auto object-contain sidebar-transition"
          />
        ) : (
          <img
            src="https://assets.dcvfd.org/ember/EmberAI.png"
            alt="Ember"
            className="h-16 w-auto object-contain sidebar-transition"
          />
        )}
      </div>

      {/* ── Navigation ───────────────────────────────────────────── */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        {visibleTree.map((node, idx) => {
          const topSpacing = idx > 0 ? "mt-2" : "";

          if (node.kind === "link") {
            return (
              <div key={node.item.to} className={topSpacing}>
                <SidebarNavItem item={node.item} collapsed={collapsed} />
              </div>
            );
          }

          if (node.kind === "committee") {
            const isActive = isCommitteeActive(node);
            const expanded = collapsed ? true : (openMap[node.key] ?? isActive);
            return (
              <div key={node.key} className={topSpacing}>
                {collapsed && idx > 0 && (
                  <div className="border-t border-theme-subtle my-3" />
                )}
                <CommitteeHeading
                  node={node}
                  expanded={expanded}
                  onToggle={() => toggleCommittee(node.key)}
                  collapsed={collapsed}
                  active={isActive}
                />
                {expanded && (
                  <div
                    className={`space-y-0.5 ${collapsed ? "mt-1.5" : "mt-1 ml-3 pl-2 border-l border-theme-subtle"}`}
                  >
                    {renderCommitteeItems(node.items, collapsed)}
                  </div>
                )}
              </div>
            );
          }

          // ─── section (Operations / Administration) ───
          const sectionExpanded = collapsed
            ? true
            : (sectionOpen[node.key] ?? node.defaultOpen);
          return (
            <div
              key={node.key}
              className={`${topSpacing} ${!collapsed && idx > 0 ? "pt-3 mt-3 border-t border-theme-subtle" : ""}`}
            >
              <SectionHeader
                label={node.label}
                expanded={sectionExpanded}
                onToggle={() => toggleSection(node.key)}
                collapsed={collapsed}
              />
              {sectionExpanded && (
                <div className={collapsed ? "" : "mt-1"}>
                  {node.children.map((child) => {
                    if (child.kind === "link") {
                      return (
                        <div key={child.item.to} className="mb-0.5">
                          <SidebarNavItem
                            item={child.item}
                            collapsed={collapsed}
                          />
                        </div>
                      );
                    }
                    const isActive = isCommitteeActive(child);
                    const expanded = collapsed
                      ? true
                      : (openMap[child.key] ?? isActive);
                    return (
                      <div key={child.key} className="mb-0.5">
                        <CommitteeHeading
                          node={child}
                          expanded={expanded}
                          onToggle={() => toggleCommittee(child.key)}
                          collapsed={collapsed}
                          active={isActive}
                        />
                        {expanded && (
                          <div
                            className={`space-y-0.5 ${collapsed ? "mt-1.5" : "mt-1 ml-3 pl-2 border-l border-theme-subtle"}`}
                          >
                            {renderCommitteeItems(child.items, collapsed)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* ── Theme toggle ─────────────────────────────────────────── */}
      <div className="px-2 pb-2">
        <button
          onClick={cycleTheme}
          title={`Theme: ${THEME_LABELS[theme]} (click to cycle)`}
          className={`w-full flex items-center rounded-lg text-sm font-medium transition-colors text-theme-secondary hover:text-theme-primary hover:bg-theme-overlay ${
            collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
          }`}
        >
          <ThemeIcon theme={theme} />
          {!collapsed && <span>Theme: {THEME_LABELS[theme]}</span>}
        </button>
      </div>

      {/* ── User section ─────────────────────────────────────────── */}
      {user && (
        <div className={`px-2 py-3 border-t border-theme-subtle shrink-0`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <UserAvatar photoUrl={user.photoUrl} name={user.name} size={32} />
              <button
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
                className="p-1.5 rounded-md text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors"
              >
                <LogoutIcon size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
              <UserAvatar photoUrl={user.photoUrl} name={user.name} size={36} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-theme-primary truncate leading-tight">
                  {user.name}
                </p>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-dcvfd/10 text-brand-adaptive capitalize mt-0.5">
                  {user.role}
                </span>
              </div>
              <button
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
                className="shrink-0 p-1.5 rounded-md text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors"
              >
                <LogoutIcon />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Collapse toggle — floats on the right edge, vertically centered ── */}
      <button
        onClick={onToggleCollapse}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute top-1/2 -translate-y-1/2 -right-3.5 z-50 w-7 h-7 rounded-full bg-theme-raised border border-theme-default flex items-center justify-center text-theme-muted hover:text-theme-primary transition-colors shadow-md"
      >
        {collapsed ? (
          <ChevronRightIcon size={14} />
        ) : (
          <ChevronLeftIcon size={14} />
        )}
      </button>
    </aside>
  );
}

// ── Mobile slide-over ────────────────────────────────────────────────────────

function MobileSidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { user, logout } = useAuth();
  const { theme, cycleTheme } = useTheme();
  const location = useLocation();

  const visibleTree = useMemo(
    () => filterNavTree(NAV_TREE, user?.role, user?.permissions),
    [user?.role, user?.permissions],
  );

  const [openMap, toggleCommittee] = useCommitteeOpenState(visibleTree);
  const [sectionOpen, toggleSection] = useSectionOpenState(visibleTree);

  function isCommitteeActive(node: CommitteeNav): boolean {
    return node.items.some(
      (i) =>
        isLinkItem(i) && !i.comingSoon && location.pathname.startsWith(i.to),
    );
  }

  // Close on Escape — mobile-only handler
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-black/50 sidebar-overlay ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 flex flex-col bg-theme-raised border-r border-theme-subtle sidebar-transition ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-theme-subtle shrink-0">
          <img
            src="https://assets.dcvfd.org/images/patch.svg"
            alt="DCVFD"
            width={36}
            height={36}
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-theme-primary truncate leading-tight">
              Ember — DCVFD Data Platform
            </p>
            <p className="text-xs text-theme-muted truncate">ember.dcvfd.org</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="shrink-0 p-1.5 rounded-md text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors"
          >
            <XIcon />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {visibleTree.map((node, idx) => {
            const topSpacing = idx > 0 ? "mt-2" : "";

            if (node.kind === "link") {
              return (
                <div key={node.item.to} className={topSpacing}>
                  <SidebarNavItem
                    item={node.item}
                    collapsed={false}
                    onClick={onClose}
                  />
                </div>
              );
            }

            if (node.kind === "committee") {
              const isActive = isCommitteeActive(node);
              const expanded = openMap[node.key] ?? isActive;
              return (
                <div key={node.key} className={topSpacing}>
                  <CommitteeHeading
                    node={node}
                    expanded={expanded}
                    onToggle={() => toggleCommittee(node.key)}
                    collapsed={false}
                    active={isActive}
                  />
                  {expanded && (
                    <div className="space-y-0.5 mt-1 ml-3 pl-2 border-l border-theme-subtle">
                      {renderCommitteeItems(node.items, false, onClose)}
                    </div>
                  )}
                </div>
              );
            }

            // section (Operations / Administration) — collapsible
            const sectionExpanded = sectionOpen[node.key] ?? node.defaultOpen;
            return (
              <div
                key={node.key}
                className={`${topSpacing} ${idx > 0 ? "pt-3 mt-3 border-t border-theme-subtle" : ""}`}
              >
                <SectionHeader
                  label={node.label}
                  expanded={sectionExpanded}
                  onToggle={() => toggleSection(node.key)}
                  collapsed={false}
                />
                {sectionExpanded && (
                  <div className="mt-1">
                    {node.children.map((child) => {
                      if (child.kind === "link") {
                        return (
                          <div key={child.item.to} className="mb-0.5">
                            <SidebarNavItem
                              item={child.item}
                              collapsed={false}
                              onClick={onClose}
                            />
                          </div>
                        );
                      }
                      const isActive = isCommitteeActive(child);
                      const expanded = openMap[child.key] ?? isActive;
                      return (
                        <div key={child.key} className="mb-0.5">
                          <CommitteeHeading
                            node={child}
                            expanded={expanded}
                            onToggle={() => toggleCommittee(child.key)}
                            collapsed={false}
                            active={isActive}
                          />
                          {expanded && (
                            <div className="space-y-0.5 mt-1 ml-3 pl-2 border-l border-theme-subtle">
                              {renderCommitteeItems(
                                child.items,
                                false,
                                onClose,
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Theme toggle */}
        <div className="px-2 pb-2">
          <button
            onClick={cycleTheme}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-theme-secondary hover:text-theme-primary hover:bg-theme-overlay"
          >
            <ThemeIcon theme={theme} />
            <span>Theme: {THEME_LABELS[theme]}</span>
          </button>
        </div>

        {/* User footer */}
        {user && (
          <div className="px-2 py-3 border-t border-theme-subtle shrink-0">
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
              <UserAvatar photoUrl={user.photoUrl} name={user.name} size={36} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-theme-primary truncate leading-tight">
                  {user.name}
                </p>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-dcvfd/10 text-brand-adaptive capitalize mt-0.5">
                  {user.role}
                </span>
              </div>
              <button
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
                className="shrink-0 p-1.5 rounded-md text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors"
              >
                <LogoutIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Mobile top bar ───────────────────────────────────────────────────────────

function MobileTopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { theme, cycleTheme } = useTheme();
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 bg-theme-raised border-b border-theme-subtle">
      <button
        onClick={onOpenMenu}
        className="p-1.5 rounded-md text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors"
        aria-label="Open menu"
      >
        <HamburgerIcon />
      </button>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <img
          src="https://assets.dcvfd.org/images/patch.svg"
          alt="DCVFD"
          width={24}
          height={24}
          className="shrink-0"
        />
        <span className="text-sm font-semibold text-theme-primary truncate">
          Data Platform
        </span>
      </div>
      <button
        onClick={cycleTheme}
        title={`Theme: ${THEME_LABELS[theme]}`}
        aria-label={`Switch theme (current: ${THEME_LABELS[theme]})`}
        className="p-1.5 rounded-md text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors shrink-0"
      >
        <ThemeIcon theme={theme} size={18} />
      </button>
    </header>
  );
}

// ── Root layout ──────────────────────────────────────────────────────────────

export default function Layout({ children, role: _role }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  // Track whether viewport is desktop (lg = 1024px) — used to suppress sidebar margin on mobile
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 1024px)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const handleToggleCollapse = () => {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // best-effort
      }
      return next;
    });
  };

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W;

  return (
    <div className="min-h-dvh bg-theme-surface overflow-x-hidden">
      {/* Desktop sidebar */}
      <Sidebar collapsed={collapsed} onToggleCollapse={handleToggleCollapse} />

      {/* Mobile top bar */}
      <MobileTopBar onOpenMenu={() => setMobileOpen(true)} />

      {/* Mobile slide-over */}
      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} />

      {/* Main content — sidebar is hidden on mobile so no left margin needed there */}
      <main
        className="lg:transition-[margin] lg:duration-[250ms] pb-16 lg:pb-0 pt-14 lg:pt-0 min-w-0"
        style={{ marginLeft: isDesktop ? `${sidebarWidth}px` : 0 }}
      >
        <div>{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <NavBar />
    </div>
  );
}
